import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import ICatalog_Item from "../../../models/ICatalog_Item";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../models/EndpointsEnum";
import ICatalog from "../../../models/ICatalog";
import IOnGetCatalogsDTO from "../../../models/DTO/IOnGetCatalogsDTO";
import IOnTypeaheadCatalogItemDTO from "../../../models/DTO/IOnTypeaheadCatalogItemDTO";
import ITypeaheadCatalogItemDTO from "../../../models/DTO/ITypeaheadCatalogItemDTO";
import IGetCatalogsDTO from "../../../models/DTO/IGetCatalogsDTO";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import Typeahead from "../../shared/Typeahead/Typeahead";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwButton from "../../shared/OwButton/OwButton";
import IProcessWarehouseTransferItemDTO from "../../../models/DTO/IProcessWarehouseTransferItemDTO";
import IWarehouse_Transfer from "../../../models/IWarehouse_Transfer";

type Props = {
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    closeFn: (refresh?: boolean) => void;
    transfer: IWarehouse_Transfer;
};

export const TransferDetailAddItemSidebar = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
    const [selectedCatalog, setSelectedCatalog] = useState<ICatalog | null>(null);
    const [selectedItem, setSelectedItem] = useState<ICatalog_Item | null>(null);
    const [foundCatalogItems, setFoundCatalogItems] = useState<ICatalog_Item[]>([]);
    const [clearTypeahead, setClearTypeahead] = useState<boolean>(false);
    const [qty, setQty] = useState(0);
    const [isSaveActive, setIsSaveActive] = useState(false);

    useEffect(() => {
        console.log("selectedCatalog", selectedCatalog);
    }, [selectedCatalog]);

    const clearTypeaheadFn = () => {
        setClearTypeahead(true);
        setFoundCatalogItems([]);
    };

    const getCatalogs = () => {
        const payload: IGetCatalogsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        props.socket.emit(EndpointsEnum.CatalogsGetCatalogs, payload);
    };

    const onGetCatalogs = (data: IOnGetCatalogsDTO) => {
        setCatalogs(data.catalogs);
    };

    const getSelectedCatalog = () => {
        return selectedCatalog;
    };

    const getCatalogItem = (name: string) => {
        if (selectedCatalog) {
            const payload: ITypeaheadCatalogItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                catalogGuid: selectedCatalog.guid,
                name,
                options: {
                    searchName: true,
                    searchDescription: true,
                    searchSku: true,
                    searchUpc: true,
                },
            };

            props.socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, payload);

            console.log("getCatalogItem", payload);
        }
    };

    const onGetCatalogItem = (data: IOnTypeaheadCatalogItemDTO) => {
        console.log("foundCatalogItems", data);
        setFoundCatalogItems(data.catalog_items);
    };

    const onProcessWarehouseTransferItem = () => {
        setIsSaveActive(false);
        props.closeFn(true);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CatalogsOnGetCatalogs, fn: onGetCatalogs },
        { endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onGetCatalogItem },
        { endpoint: EndpointsEnum.WarehouseOnProcessWarehouseTransferItem, fn: onProcessWarehouseTransferItem },
    ];

    useEffect(() => {
        for (const socket of sockets) {
            props.socket.on(socket.endpoint, socket.fn);
        }

        getCatalogs();

        return () => {
            for (const socket of sockets) {
                props.socket.removeListener(socket.endpoint, socket.fn);
            }
        };
    }, []);

    const cancel = () => {
        props.closeFn();
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        switch (name) {
            case "catalog": {
                const catalog = catalogs.find((c) => c.guid === value);
                setSelectedCatalog(catalog || null);
                break;
            }

            case "qty":
                setQty(Number(value));
                break;
        }
    };

    const selectedItemFn = (item: ICatalog_Item) => {
        // console.log('selectedItemFn: ', item);
        setSelectedItem(item);
    };

    const saveFn = () => {
        if (selectedItem) {
            const payload: IProcessWarehouseTransferItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                catalog_item_id: selectedItem?.id,
                warehouse_transfer_id: props.transfer.id,
                sent_qty: qty,
            };

            props.socket.emit(EndpointsEnum.WarehouseProcessWarehouseTransferItem, payload);
        }
    };

    return (
        <div className={"TransferDetailAddItemSidebar p-3"}>
            <div className={"row"}>
                <div className={"col-12"}>
                    <div className="text-end">
                        <button className="btn btn-danger btn-sm" onClick={cancel}>
                            <i className="far fa-times" />
                        </button>
                    </div>
                    <OwTitle className={"mb-2"} leftIconName={"fad fa-plus"} title={"add_item"} />
                    <hr />
                </div>
            </div>

            <div>
                <div className={"mb-2"}>
                    <OwLabel title={"catalog"} />
                    <OwSelectOption fn={handleChange} titleProp={"name"} valueProp={"guid"} options={catalogs} name={"catalog"} />
                </div>

                {selectedCatalog ? (
                    <div className={"mb-2"}>
                        <OwLabel title={"item"} />
                        <Typeahead
                            isSelectableFn={() => true}
                            typeaheadApiFn={getCatalogItem}
                            titleProp={"name"}
                            subTitleProp={"sku"}
                            typeaheadItems={foundCatalogItems}
                            clearTypeahead={clearTypeahead}
                            clearTypeaheadCallback={clearTypeaheadFn}
                            selectedItemFn={selectedItemFn}
                        />
                    </div>
                ) : null}

                <div className={"mb-2"}>
                    <OwLabel title={"qty"} />
                    <OwFnInput fn={handleChange} name={"qty"} value={qty} type={"number"} />
                </div>

                <div className="mb-2">
                    <OwButton title={"save"} fn={saveFn} active={isSaveActive} disableOnActive={true} />
                </div>
            </div>
        </div>
    );
};
